import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, Search, Target, CheckCircle2, Loader2, ChevronDown, ChevronUp, ExternalLink, Sparkles, FlaskConical, Building2, Microscope, Shield, Zap, Globe, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const WORKSTREAMS = [
  { value: 'all', label: 'All Workstreams', icon: Globe },
  { value: 'ndds', label: 'Formulation / Delivery Technologies', icon: FlaskConical },
  { value: 'academic', label: 'Academic Translational Assets', icon: Microscope },
  { value: 'dtx', label: 'DTx / Companion Digital', icon: Zap },
];

const STAGE_STEPS = [
  { key: 'scouting', label: 'Workstream Scout', icon: Search, desc: 'Scanning assets by workstream bucket' },
  { key: 'screening', label: 'Medical Screening', icon: Microscope, desc: 'Generating 5-section briefs with web intelligence' },
  { key: 'scoring', label: 'Partner-Fit Scoring', icon: Target, desc: 'Scoring against buyer priorities' },
  { key: 'recommending', label: 'Deal Readiness', icon: CheckCircle2, desc: 'Generating recommendations' },
];

const REC_COLORS = {
  prioritize: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  contact: 'bg-sky-100 text-sky-800 border-sky-300',
  watch: 'bg-amber-100 text-amber-800 border-amber-300',
  discard: 'bg-slate-100 text-slate-500 border-slate-300',
};

const REC_LABELS = { prioritize: 'Prioritize for Leadership', contact: 'Contact', watch: 'Watch', discard: 'Discard' };

const SCORE_BAR_COLOR = (score) => {
  if (score >= 75) return 'bg-emerald-500';
  if (score >= 55) return 'bg-sky-500';
  if (score >= 35) return 'bg-amber-500';
  return 'bg-slate-400';
};

function AssetCard({ asset, brief, score, recommendation, index }) {
  const [expanded, setExpanded] = useState(false);
  const total = score?.total_score || 0;
  const rec = recommendation?.recommendation || 'watch';

  return (
    <Card className={`border transition-all duration-300 ${expanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'} ${rec === 'prioritize' ? 'border-emerald-200' : rec === 'contact' ? 'border-sky-200' : 'border-slate-200'}`} data-testid={`asset-card-${index}`}>
      <CardContent className="p-0">
        <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 text-slate-500 text-xs font-bold shrink-0">{index + 1}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm text-slate-900">{asset.asset_name}</span>
              <span className="text-xs text-slate-400">by {asset.company_name}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="outline" className="text-[10px] py-0">{asset.stage}</Badge>
              {asset.therapeutic_area && <Badge className="text-[10px] py-0 bg-violet-50 text-violet-700 border border-violet-200">{asset.therapeutic_area}</Badge>}
              {asset.modality && <Badge className="text-[10px] py-0 bg-slate-50 text-slate-600 border border-slate-200">{asset.modality}</Badge>}
              <span className="text-xs text-slate-500 truncate">{asset.indication?.substring(0, 70)}{asset.indication?.length > 70 ? '...' : ''}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${SCORE_BAR_COLOR(total)}`} style={{ width: `${total}%` }} />
                </div>
                <span className="text-xs font-bold text-slate-700 w-8">{total}</span>
              </div>
            </div>
            <Badge className={`text-[9px] border ${REC_COLORS[rec]}`}>{REC_LABELS[rec]}</Badge>
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>

        {expanded && (
          <div className="border-t border-slate-100 p-5 space-y-4 bg-slate-50/30">
            {/* Asset meta */}
            <div className="flex flex-wrap gap-3 text-xs">
              {asset.therapeutic_area && <span className="px-2.5 py-1 rounded bg-violet-50 text-violet-700 font-semibold border border-violet-200">Therapy Area: {asset.therapeutic_area}</span>}
              {asset.modality && <span className="px-2.5 py-1 rounded bg-slate-50 text-slate-600 font-semibold border border-slate-200">Modality: {asset.modality}</span>}
              {asset.stage && <span className="px-2.5 py-1 rounded bg-sky-50 text-sky-700 font-semibold border border-sky-200">Stage: {asset.stage}</span>}
              {asset.workstream && asset.workstream !== 'all' && <span className="px-2.5 py-1 rounded bg-amber-50 text-amber-700 font-semibold border border-amber-200">Workstream: {asset.workstream}</span>}
            </div>
            {/* Evidence metadata */}
            {(brief?.evidence_confidence || brief?.stage_certainty || brief?.rights_availability) && (
              <div className="flex flex-wrap gap-2 text-[10px]">
                {brief?.evidence_confidence && <span className={`px-2 py-0.5 rounded border ${brief.evidence_confidence === 'High' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : brief.evidence_confidence === 'Medium' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-red-50 text-red-600 border-red-200'}`}>Evidence: {brief.evidence_confidence}</span>}
                {brief?.stage_certainty && <span className={`px-2 py-0.5 rounded border ${brief.stage_certainty === 'Confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : brief.stage_certainty === 'Inferred' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-600 border-red-200'}`}>Stage: {brief.stage_certainty}</span>}
                {brief?.rights_availability && <span className={`px-2 py-0.5 rounded border ${brief.rights_availability === 'Confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : brief.rights_availability === 'Possible' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>Rights: {brief.rights_availability}</span>}
              </div>
            )}
            {/* Score breakdown */}
            {score && (
              <div className="grid grid-cols-5 gap-3">
                {[
                  { label: 'Therapy Area', val: score.therapy_area_score },
                  { label: 'Stage Fit', val: score.stage_score },
                  { label: 'Commercial', val: score.commercial_score },
                  { label: 'LCM', val: score.lcm_score },
                  { label: 'Adjacency', val: score.adjacency_score },
                ].map((d, i) => (
                  <div key={i} className="text-center">
                    <div className="text-lg font-bold text-slate-800">{d.val || 0}<span className="text-[10px] text-slate-400 font-normal">/20</span></div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">{d.label}</div>
                  </div>
                ))}
              </div>
            )}
            {score?.rationale && <p className="text-sm text-slate-600 italic bg-white rounded-lg p-3 border border-slate-100">{score.rationale}</p>}

            {/* Medical Brief */}
            {brief && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Medical Screening Brief</p>
                {[
                  { key: 'why_it_fits_buyer', label: 'Why It Fits ' + (asset.company_name || 'Buyer'), color: 'border-l-emerald-500' },
                  { key: 'unmet_need', altKey: 'why_it_matters', label: 'Why It Matters / Unmet Need', color: 'border-l-red-400' },
                  { key: 'product_summary', altKey: 'asset_snapshot', label: 'Asset Snapshot', color: 'border-l-sky-400' },
                  { key: 'clinical_evidence', altKey: 'evidence_summary', label: 'Evidence Summary', color: 'border-l-emerald-400' },
                  { key: 'competitive_landscape', altKey: 'competitive_context', label: 'Competitive / Portfolio Context', color: 'border-l-amber-400' },
                  { key: 'recent_updates', label: 'Recent Verified Updates', color: 'border-l-violet-400' },
                  { key: 'risks_unknowns', label: 'Risks / Unknowns', color: 'border-l-red-500' },
                ].map(section => {
                  const text = brief[section.key] || (section.altKey ? brief[section.altKey] : '') || '';
                  return text ? (
                  <div key={section.key} className={`bg-white rounded-lg p-4 border border-slate-100 border-l-4 ${section.color}`}>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">{section.label}</p>
                    <p className="text-sm text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(text.replace(/\[(\d+)\]/g, '<sup class="text-sky-600 font-bold text-[9px] cursor-pointer">[$1]</sup>'))
                    }} />
                  </div>
                ) : null;
                })}
                {brief.sources?.length > 0 && (
                  <div className="bg-white rounded-lg p-3 border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">References</p>
                    <div className="space-y-1">
                      {brief.sources.slice(0, 8).map((url, i) => (
                        <div key={i} className="flex items-start gap-1.5">
                          <span className="text-[10px] font-bold text-sky-600 shrink-0">[{i+1}]</span>
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-sky-600 underline break-all">
                            {url}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action note */}
            {recommendation?.action_note && (
              <div className="bg-white rounded-lg p-3 border border-slate-100 border-l-4 border-l-emerald-500">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Recommended Action</p>
                <p className="text-sm text-slate-700">{recommendation.action_note}</p>
              </div>
            )}
            {/* Red flags */}
            {recommendation?.red_flags?.length > 0 && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider mb-1">Red Flags</p>
                <ul className="space-y-0.5">
                  {recommendation.red_flags.map((f, i) => (
                    <li key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-red-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function IntelligenceHubPage() {
  const [buyers, setBuyers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [selectedTAs, setSelectedTAs] = useState([]);
  const [selectedWorkstreams, setSelectedWorkstreams] = useState(['all']);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedModalities, setSelectedModalities] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [running, setRunning] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runData, setRunData] = useState(null);
  const [pastRuns, setPastRuns] = useState([]);
  const [taDropdownOpen, setTaDropdownOpen] = useState(false);
  const [wsDropdownOpen, setWsDropdownOpen] = useState(false);
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [modalityDropdownOpen, setModalityDropdownOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [buyerDropdownOpen, setBuyerDropdownOpen] = useState(false);
  const [buyerSearch, setBuyerSearch] = useState('');

  useEffect(() => {
    axios.get(`${API}/agents/pharma-buyers`, { withCredentials: true })
      .then(res => setBuyers(res.data || []))
      .catch(() => {});
    axios.get(`${API}/agents/runs`, { withCredentials: true })
      .then(res => setPastRuns(res.data || []))
      .catch(() => {});
    axios.get(`${API}/agents/asset-countries`, { withCredentials: true })
      .then(res => setCountries(res.data || []))
      .catch(() => {});
  }, []);

  // Poll for run progress
  useEffect(() => {
    if (!runId || !running) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/agents/runs/${runId}`, { withCredentials: true });
        setRunData(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setRunning(false);
          if (res.data.status === 'completed') toast.success('Pipeline completed');
          if (res.data.status === 'failed') toast.error(res.data.error || 'Pipeline failed');
          // Refresh Recent Runs so the completed run moves into the accordion list
          try {
            const rs = await axios.get(`${API}/agents/runs`, { withCredentials: true });
            setPastRuns(rs.data || []);
          } catch {}
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [runId, running]);

  // Smooth "creeping" progress so the bar shows motion between backend checkpoints
  const [displayProgress, setDisplayProgress] = useState(0);
  useEffect(() => {
    if (!running) { setDisplayProgress(runData?.progress || 0); return; }
    const real = runData?.progress || 0;
    const status = runData?.status || 'starting';
    // Each stage has a ceiling the creep stops at (leaves room for real jump on next poll)
    const ceilings = { starting: 28, scouting: 28, screening: 63, scoring: 83, recommending: 98, completed: 100, failed: 100 };
    const ceiling = ceilings[status] ?? 99;
    setDisplayProgress(prev => Math.max(prev, real));
    const tick = setInterval(() => {
      setDisplayProgress(prev => {
        const target = Math.max(real, prev);
        if (target >= ceiling) return target;
        return Math.min(ceiling, target + 0.4);
      });
    }, 500);
    return () => clearInterval(tick);
  }, [runData, running]);

  const ALL_TAS = ['CNS', 'Cardiovascular', 'Oncology', 'Immunology', 'Dermatology', 'Ophthalmology', 'Respiratory', 'Metabolic', 'Rare Disease', 'Neurology', 'Pain', 'Gastrointestinal', 'Infectious Disease', 'Hematology', 'Endocrinology', 'Nephrology', 'Rheumatology', 'Psychiatry'];

  const ALL_STAGES = ['Commercial', 'Phase 3', 'Phase 2', 'Phase 1', 'IND', 'Pre-Clinical', 'Research'];

  const ALL_MODALITIES = ['Small molecule', 'Biologic', 'mAb', 'ADC', 'Gene Therapy', 'Cell Therapy', 'RNA / ASO', 'Peptide', 'Protein', 'Biosimilar', 'Vaccine', 'Antibody', 'Fusion Protein', 'Oligonucleotide'];

  const toggleTA = (ta) => {
    setSelectedTAs(prev => prev.includes(ta) ? prev.filter(t => t !== ta) : [...prev, ta]);
  };
  const toggleWS = (ws) => {
    setSelectedWorkstreams(prev => {
      if (ws === 'all') return ['all'];
      const next = prev.filter(w => w !== 'all');
      return next.includes(ws) ? next.filter(w => w !== ws) : [...next, ws];
    });
  };
  const toggleStage = (s) => {
    setSelectedStages(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };
  const toggleModality = (m) => {
    setSelectedModalities(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };
  const toggleCountry = (c) => {
    setSelectedCountries(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const startPipeline = async () => {
    if (!selectedBuyer) { toast.error('Select a pharma buyer'); return; }
    setRunning(true);
    setRunData(null);
    try {
      const res = await axios.post(`${API}/agents/run-pipeline`, {
        workstreams: selectedWorkstreams.length ? selectedWorkstreams : ['all'],
        buyer_id: selectedBuyer,
        therapy_areas: selectedTAs,
        stages: selectedStages,
        modalities: selectedModalities,
        countries: selectedCountries,
        limit: 15,
      }, { withCredentials: true });
      setRunId(res.data.run_id);
      toast.success('Pipeline started — agents are working');
    } catch (e) {
      toast.error('Failed to start pipeline');
      setRunning(false);
    }
  };

  const loadPastRun = async (id) => {
    try {
      const res = await axios.get(`${API}/agents/runs/${id}`, { withCredentials: true });
      setRunData(res.data);
      setRunId(id);
      setRunning(false);
    } catch { toast.error('Failed to load run'); }
  };

  // Build sorted results
  const results = React.useMemo(() => {
    if (!runData?.scouted_assets) return [];
    const assets = runData.scouted_assets;
    const briefs = runData.briefs || {};
    const scores = runData.scores || {};
    const recs = runData.recommendations || {};

    return assets.map(a => {
      const s = scores[a.asset_id] || {};
      const r = recs[a.asset_name] || {};
      return { asset: a, brief: briefs[a.asset_id], score: s, recommendation: r, total: s.total_score || 0 };
    }).sort((a, b) => b.total - a.total);
  }, [runData]);

  const recCounts = React.useMemo(() => {
    const c = { prioritize: 0, contact: 0, watch: 0, discard: 0 };
    results.forEach(r => { const k = r.recommendation?.recommendation; if (k && c[k] !== undefined) c[k]++; });
    return c;
  }, [results]);

  const currentStep = STAGE_STEPS.findIndex(s => s.key === runData?.status);
  const buyerName = buyers.find(b => b.id === (selectedBuyer || runData?.buyer_id))?.name || '';

  return (
    <div className="space-y-6 pl-2" data-testid="intelligence-hub">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2.5 mb-1.5">
          <Brain className="w-5 h-5 text-sky-600" />
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Intelligence Hub</h1>
          <Badge className="bg-sky-50 text-sky-700 border border-sky-200 text-[9px]">4-Agent Pipeline</Badge>
        </div>
        <p className="text-sm text-slate-500">Scout assets, screen medical evidence, score partner-fit, and generate deal recommendations — powered by AI agents.</p>
      </div>

      {/* Variance disclaimer */}
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="variance-notice">
        <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-[11px] text-amber-800 leading-snug">
          <span className="font-semibold">Scores may vary ±3-10 points between runs.</span> Each run pulls fresh data from the open web and re-analyzes the evidence, so identical inputs can produce slightly different scores. Use the top-5 ranking as the stable signal; treat exact scores as directional.
        </p>
      </div>

      {/* Controls */}
      <Card className="border border-slate-200" data-testid="pipeline-controls">
        <CardContent className="p-5 space-y-4">
          {/* Row 1: Buyer — searchable combobox */}
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Pharma Buyer</label>
            <div className="relative" data-testid="buyer-combobox">
              <button
                type="button"
                onClick={() => { setBuyerDropdownOpen(o => !o); setBuyerSearch(''); }}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-left hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-200"
                data-testid="buyer-trigger">
                <span className={selectedBuyer ? 'text-slate-900' : 'text-slate-400'}>
                  {selectedBuyer ? (buyers.find(b => b.id === selectedBuyer)?.name || 'Select buyer company') : 'Select buyer company'}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${buyerDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {buyerDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setBuyerDropdownOpen(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-100">
                      <input
                        type="text"
                        autoFocus
                        placeholder={`Search ${buyers.length} companies...`}
                        value={buyerSearch}
                        onChange={(e) => setBuyerSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-sky-200"
                        data-testid="buyer-search-input"
                      />
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {buyers.filter(b => !buyerSearch || b.name.toLowerCase().includes(buyerSearch.toLowerCase())).slice(0, 200).map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => { setSelectedBuyer(b.id); setBuyerDropdownOpen(false); setBuyerSearch(''); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${selectedBuyer === b.id ? 'bg-sky-50 text-sky-900' : 'text-slate-700'}`}
                          data-testid={`buyer-option-${b.id}`}>
                          {b.name}
                        </button>
                      ))}
                      {buyers.filter(b => !buyerSearch || b.name.toLowerCase().includes(buyerSearch.toLowerCase())).length === 0 && (
                        <p className="px-3 py-4 text-xs text-slate-400 text-center">No companies match "{buyerSearch}"</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Row 2: Workstreams + Therapeutic Areas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Workstreams multi-select */}
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Workstreams</label>
              <div className="relative">
                <button type="button" onClick={() => { setWsDropdownOpen(!wsDropdownOpen); setTaDropdownOpen(false); setStageDropdownOpen(false); setModalityDropdownOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-slate-700 hover:border-slate-300 transition-colors" data-testid="ws-dropdown-trigger">
                  <span className={selectedWorkstreams.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
                    {selectedWorkstreams.includes('all') ? 'All Assets' : selectedWorkstreams.map(w => WORKSTREAMS.find(x => x.value === w)?.label || w).join(', ')}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${wsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {wsDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {WORKSTREAMS.map(w => (
                        <button key={w.value} type="button" onClick={() => toggleWS(w.value)}
                          className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${selectedWorkstreams.includes(w.value) ? 'bg-sky-100 text-sky-700 border-sky-300' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-200 hover:bg-sky-50'}`}>
                          {w.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-100">
                      <button type="button" onClick={() => setSelectedWorkstreams(['all'])} className="text-xs text-slate-400 hover:text-red-500">Reset</button>
                      <button type="button" onClick={() => setWsDropdownOpen(false)} className="ml-auto px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800">Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Therapeutic Areas */}
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Therapeutic Areas</label>
              <div className="relative">
                <button type="button" onClick={() => { setTaDropdownOpen(!taDropdownOpen); setWsDropdownOpen(false); setStageDropdownOpen(false); setModalityDropdownOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-slate-700 hover:border-slate-300 transition-colors" data-testid="ta-dropdown-trigger">
                  <span className={selectedTAs.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
                    {selectedTAs.length ? selectedTAs.join(', ') : 'All therapeutic areas'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${taDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {taDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-h-48 overflow-y-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_TAS.map(ta => (
                        <button key={ta} type="button" onClick={() => toggleTA(ta)}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${selectedTAs.includes(ta) ? 'bg-sky-100 text-sky-700 border-sky-300' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-200 hover:bg-sky-50'}`}>
                          {ta}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-100">
                      {selectedTAs.length > 0 && <button type="button" onClick={() => setSelectedTAs([])} className="text-xs text-slate-400 hover:text-red-500">Clear all</button>}
                      <button type="button" onClick={() => setTaDropdownOpen(false)} className="ml-auto px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800" data-testid="ta-done-btn">Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Stage + Modality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Stage filter */}
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Phase / Stage</label>
              <div className="relative">
                <button type="button" onClick={() => { setStageDropdownOpen(!stageDropdownOpen); setWsDropdownOpen(false); setTaDropdownOpen(false); setModalityDropdownOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-slate-700 hover:border-slate-300 transition-colors" data-testid="stage-dropdown-trigger">
                  <span className={selectedStages.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
                    {selectedStages.length ? selectedStages.join(', ') : 'All stages'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${stageDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {stageDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_STAGES.map(s => (
                        <button key={s} type="button" onClick={() => toggleStage(s)}
                          className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${selectedStages.includes(s) ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-200 hover:bg-emerald-50'}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-100">
                      {selectedStages.length > 0 && <button type="button" onClick={() => setSelectedStages([])} className="text-xs text-slate-400 hover:text-red-500">Clear all</button>}
                      <button type="button" onClick={() => setStageDropdownOpen(false)} className="ml-auto px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800">Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Modality filter */}
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Modality</label>
              <div className="relative">
                <button type="button" onClick={() => { setModalityDropdownOpen(!modalityDropdownOpen); setWsDropdownOpen(false); setTaDropdownOpen(false); setStageDropdownOpen(false); }}
                  className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-slate-700 hover:border-slate-300 transition-colors" data-testid="modality-dropdown-trigger">
                  <span className={selectedModalities.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
                    {selectedModalities.length ? selectedModalities.join(', ') : 'All modalities'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${modalityDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {modalityDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_MODALITIES.map(m => (
                        <button key={m} type="button" onClick={() => toggleModality(m)}
                          className={`px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${selectedModalities.includes(m) ? 'bg-violet-100 text-violet-700 border-violet-300' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-200 hover:bg-violet-50'}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-slate-100">
                      {selectedModalities.length > 0 && <button type="button" onClick={() => setSelectedModalities([])} className="text-xs text-slate-400 hover:text-red-500">Clear all</button>}
                      <button type="button" onClick={() => setModalityDropdownOpen(false)} className="ml-auto px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800">Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Row 4: Country (asset's biotech country) */}
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 block">Country (asset origin)</label>
            <div className="relative">
              <button type="button" onClick={() => { setCountryDropdownOpen(!countryDropdownOpen); setWsDropdownOpen(false); setTaDropdownOpen(false); setStageDropdownOpen(false); setModalityDropdownOpen(false); setCountrySearch(''); }}
                className="w-full flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md bg-white text-sm text-slate-700 hover:border-slate-300 transition-colors" data-testid="country-dropdown-trigger">
                <span className={selectedCountries.length ? 'text-slate-800 truncate' : 'text-slate-400'}>
                  {selectedCountries.length ? `${selectedCountries.length} countr${selectedCountries.length === 1 ? 'y' : 'ies'}: ${selectedCountries.slice(0, 3).join(', ')}${selectedCountries.length > 3 ? '…' : ''}` : 'All countries'}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {countryDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCountryDropdownOpen(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
                    <div className="p-2 border-b border-slate-100">
                      <input
                        type="text"
                        autoFocus
                        placeholder={`Search ${countries.length} countries...`}
                        value={countrySearch}
                        onChange={(e) => setCountrySearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-amber-200"
                        data-testid="country-search-input"
                      />
                    </div>
                    <div className="overflow-y-auto flex-1 p-3">
                      <div className="flex flex-wrap gap-1.5">
                        {countries
                          .filter(c => !countrySearch || c.country.toLowerCase().includes(countrySearch.toLowerCase()))
                          .map(c => (
                            <button
                              key={c.country}
                              type="button"
                              onClick={() => toggleCountry(c.country)}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${selectedCountries.includes(c.country) ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-200 hover:bg-amber-50'}`}
                              data-testid={`country-pill-${c.country.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {c.country}
                              <span className="text-[9px] text-slate-400">{c.count}</span>
                            </button>
                          ))}
                        {countries.filter(c => !countrySearch || c.country.toLowerCase().includes(countrySearch.toLowerCase())).length === 0 && (
                          <p className="text-xs text-slate-400 py-2">No countries match "{countrySearch}"</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-2 border-t border-slate-100">
                      {selectedCountries.length > 0 && <button type="button" onClick={() => setSelectedCountries([])} className="text-xs text-slate-400 hover:text-red-500" data-testid="country-clear-btn">Clear all</button>}
                      <button type="button" onClick={() => setCountryDropdownOpen(false)} className="ml-auto px-4 py-1.5 rounded-md text-xs font-semibold bg-slate-900 text-white hover:bg-slate-800" data-testid="country-done-btn">Done</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <Button onClick={startPipeline} disabled={running || !selectedBuyer} className="bg-slate-900 text-white hover:bg-slate-800 gap-2 w-full md:w-auto" data-testid="run-pipeline-btn">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {running ? 'Agents Working...' : 'Run Analysis'}
          </Button>
        </CardContent>
      </Card>

      {/* Pipeline Progress */}
      {running && (
        <Card className="border border-sky-200 bg-sky-50/30" data-testid="pipeline-progress">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
              <span className="text-sm font-semibold text-slate-800">Pipeline Running{buyerName ? ` for ${buyerName}` : ''}</span>
              <span className="text-xs text-slate-500 ml-auto">{Math.round(displayProgress)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden mb-4 relative">
              <div className="h-full bg-sky-500 rounded-full transition-all duration-500 ease-out" style={{ width: `${Math.max(displayProgress, 2)}%` }} />
              <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)', backgroundSize: '200% 100%', animation: 'shimmer 1.8s linear infinite' }} />
            </div>
            <div className="grid grid-cols-4 gap-3">
              {STAGE_STEPS.map((step, i) => {
                const status = runData?.status || 'starting';
                const isActive = step.key === status;
                const isDone = currentStep > i || status === 'completed';
                return (
                  <div key={step.key} className={`flex items-center gap-2 p-2 rounded-lg ${isActive ? 'bg-sky-100 border border-sky-200' : isDone ? 'bg-emerald-50 border border-emerald-200' : 'bg-white border border-slate-100'}`}>
                    {isDone ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> :
                     isActive ? <Loader2 className="w-4 h-4 animate-spin text-sky-500 shrink-0" /> :
                     <step.icon className="w-4 h-4 text-slate-300 shrink-0" />}
                    <div>
                      <p className={`text-[10px] font-bold ${isActive ? 'text-sky-700' : isDone ? 'text-emerald-700' : 'text-slate-400'}`}>{step.label}</p>
                      <p className="text-[9px] text-slate-400">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            {runData?.scouted_count > 0 && <p className="text-xs text-slate-600 mt-3">Scouted {runData.scouted_count} assets — {runData?.status === 'screening' ? 'screening evidence (≈40s per asset)' : runData?.status === 'scoring' ? 'scoring partner fit (≈1 min)' : runData?.status === 'recommending' ? 'finalizing recommendations' : 'processing'}</p>}
            {!runData && <p className="text-xs text-slate-400 mt-3">Initializing agents...</p>}
            <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
          </CardContent>
        </Card>
      )}

      {/* Results shown inline when pipeline just completed (no past run selected) */}
      {runData?.status === 'completed' && results.length > 0 && !pastRuns.find(p => p.id === runId) && (
        <div className="space-y-4" data-testid="pipeline-results">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">{results.length} Assets Analyzed for {buyerName}</h2>
              <p className="text-xs text-slate-700">Workstream: {WORKSTREAMS.find(w => w.value === runData.workstream)?.label}</p>
            </div>
            <div className="flex items-center gap-2">
              {Object.entries(recCounts).filter(([,v]) => v > 0).map(([k, v]) => (
                <Badge key={k} className={`text-[9px] border ${REC_COLORS[k]}`}>{v} {REC_LABELS[k]}</Badge>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5 text-xs ml-2" data-testid="download-pptx-btn"
                onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/agents/runs/${runId}/export-pptx`, { withCredentials: true, responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `ForesightRx_Intelligence_${buyerName.replace(/\s/g, '_')}.pptx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('PowerPoint downloaded');
                  } catch { toast.error('Failed to export'); }
                }}>
                <Download className="w-3.5 h-3.5" /> Export PPTX
              </Button>
            </div>
          </div>
          {results.map((r, i) => (
            <AssetCard key={r.asset.asset_id} asset={r.asset} brief={r.brief} score={r.score} recommendation={r.recommendation} index={i} />
          ))}
        </div>
      )}

      {runData?.status === 'completed' && results.length === 0 && (
        <Card className="border border-slate-200"><CardContent className="py-12 text-center">
          <Search className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{runData.error || 'No matching assets found for this configuration.'}</p>
        </CardContent></Card>
      )}

      {/* Past Runs */}
      {!running && pastRuns.length > 0 && (
        <div data-testid="past-runs">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Recent Runs</p>
          <div className="space-y-2">
            {pastRuns.map(r => {
              const rc = r.rec_counts || {};
              const isActive = runId === r.id;
              return (
                <div key={r.id} className={`rounded-lg border transition-colors ${isActive ? 'bg-sky-50 border-sky-200' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                  <div className="flex items-center justify-between px-4 py-3">
                    <button onClick={() => isActive ? (setRunData(null), setRunId(null)) : loadPastRun(r.id)} className="flex-1 text-left" data-testid={`past-run-${r.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{r.buyer_name || 'Unknown'}</span>
                        <span className="text-xs text-slate-600">
                          {WORKSTREAMS.find(w => w.value === r.workstream)?.label || r.workstream}
                        </span>
                        {r.therapy_areas_override?.length > 0 && (
                          <span className="text-xs text-slate-500">| {r.therapy_areas_override.join(', ')}</span>
                        )}
                        <span className="text-xs text-slate-400">| {r.scouted_count || 0} assets</span>
                      </div>
                      {r.status === 'completed' && (
                        <div className="flex items-center gap-1.5 mt-1.5">
                          {rc.prioritize > 0 && <Badge className="text-[9px] border bg-emerald-100 text-emerald-800 border-emerald-300">{rc.prioritize} Prioritize</Badge>}
                          {rc.contact > 0 && <Badge className="text-[9px] border bg-sky-100 text-sky-800 border-sky-300">{rc.contact} Contact</Badge>}
                          {rc.watch > 0 && <Badge className="text-[9px] border bg-amber-100 text-amber-800 border-amber-300">{rc.watch} Watch</Badge>}
                          {rc.discard > 0 && <Badge className="text-[9px] border bg-slate-100 text-slate-500 border-slate-300">{rc.discard} Discard</Badge>}
                        </div>
                      )}
                    </button>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      {r.status === 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isActive) { setRunData(null); setRunId(null); }
                            else { loadPastRun(r.id); }
                          }}
                          className={`flex items-center justify-center w-7 h-7 rounded-md border transition-all ${isActive ? 'bg-sky-600 border-sky-600 text-white hover:bg-sky-700' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50'}`}
                          title={isActive ? 'Close results' : 'Expand results'}
                          data-testid={`toggle-run-${r.id}`}>
                          {isActive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                      )}
                      {r.status === 'completed' && (
                        <Button variant="outline" size="sm" className="gap-1 text-[10px] h-7 px-2"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await axios.get(`${API}/agents/runs/${r.id}/export-pptx`, { withCredentials: true, responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([res.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `ForesightRx_Intelligence_${(r.buyer_name || 'Report').replace(/\s/g, '_')}.pptx`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              toast.success('PowerPoint downloaded');
                            } catch { toast.error('Failed to export'); }
                          }}
                          data-testid={`export-run-${r.id}`}>
                          <Download className="w-3 h-3" /> PPTX
                        </Button>
                      )}
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm('Delete this run?')) return;
                          try {
                            await axios.delete(`${API}/agents/runs/${r.id}`, { withCredentials: true });
                            setPastRuns(prev => prev.filter(p => p.id !== r.id));
                            if (runId === r.id) { setRunData(null); setRunId(null); }
                            toast.success('Run deleted');
                          } catch { toast.error('Failed to delete'); }
                        }}
                        className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors"
                        data-testid={`delete-run-${r.id}`}>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </div>
                  {isActive && runData?.status === 'completed' && (
                    <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-4 space-y-3">
                      {results.length === 0 ? (
                        <p className="text-xs text-slate-500 text-center py-4">{runData.error || 'No matching assets found for this run.'}</p>
                      ) : (
                        results.map((res, i) => (
                          <AssetCard key={res.asset.asset_id} asset={res.asset} brief={res.brief} score={res.score} recommendation={res.recommendation} index={i} />
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
